import CandidateTable from "@/components/candidate-table"
import Kanban from "@/components/kanban"
import ViewControls from "@/components/view-controls"
import {
  EmailSendDialog,
  type EmailRecipient,
  type SchedulingOption,
} from "@/components/email-send-dialog"
import {
  calculateKanbanStats,
  cloneFeatures,
  mapApplicationsToKanbanFeatures,
  normalizeHubspotId,
} from "@/lib/kanban-data"
import StatCard from "@/components/statCard"
import { ActivityIcon, CheckCircleIcon, UsersIcon, XCircleIcon } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router"
import type { KanbanFeature, KanbanStats } from "@/types/kanban"
import {
  buildStageAssociationLookup,
  createDefaultCandidateFilters,
  type CandidateFilters,
  type CandidateStatusId,
  type StageAssociationLookup,
} from "@/types/candidate"
import { matchesCandidateFilters } from "@/lib/candidate-filters"
import { shouldHideCompanyInEmail } from "@/lib/pipeline-stage"

import { useVacancyApplications } from "@/hooks/supabase/useVacancyApplications"
import {
  useCandidateStatusUpdater,
  type CandidateStatusUpdateOutcome,
} from "@/hooks/useCandidateStatusUpdater"
import { sendCandidateInvite, type SchedulePayload } from "@/lib/email/send-candidate-invite"
import { useEmailTemplates } from "@/hooks/supabase/useEmailTemplates"
import type { Template } from "@/types/email"
import { useAuth0 } from "@auth0/auth0-react"
import { useAuth0AccessToken } from "@/hooks/useAuth0AccessToken"
import {
  DEFAULT_OFFICE_LOCATION,
  OFFICE_LOCATIONS,
  buildOfficeFooter,
  isOfficeLocation,
  type OfficeLocation,
} from "@/constants/office-locations"

const TABLE_VIEW_BREAKPOINT = 768

const getIsBelowTableViewBreakpoint = () =>
  typeof window !== "undefined" && window.innerWidth < TABLE_VIEW_BREAKPOINT

type MarkerMap = Record<string, string>

const splitNameForMarkers = (input?: string | null) => {
  if (!input) {
    return { first: null, last: null }
  }
  const segments = input.trim().split(/\s+/).filter(Boolean)
  if (segments.length === 0) {
    return { first: null, last: null }
  }
  const [first, ...rest] = segments
  return {
    first: first ?? null,
    last: rest.length > 0 ? rest.join(" ") : null,
  }
}

const DEFAULT_SENDER_EMAIL = "office@siebertwassink.nl"

const buildMarkerMap = ({
  name,
  email,
  jobTitle,
  companyName,
  companyLocation,
  vacancyName,
  senderEmail,
  location,
  phoneNumber,
  tel,
  reception,
  officeFooter,
  advisor,
  team,
}: {
  name?: string | null
  email: string
  jobTitle?: string | null
  companyName?: string | null
  companyLocation?: string | null
  vacancyName?: string | null
  senderEmail?: string | null
  location?: string | null
  phoneNumber?: string | null
  tel?: string | null
  reception?: string | null
  officeFooter?: string | null
  advisor?: string | null
  team?: string | null
}): MarkerMap => {
  const markers = new Map<string, string>()
  const displayName = name && name.trim().length > 0 ? name.trim() : email
  const { first, last } = splitNameForMarkers(displayName)
  const normalizedSender =
    senderEmail && senderEmail.trim().length > 0 ? senderEmail.trim() : DEFAULT_SENDER_EMAIL

  const setMarker = (key: string, value?: string | null) => {
    if (!value) {
      return
    }
    const normalized = value.toString().trim()
    if (!normalized) {
      return
    }
    markers.set(key, normalized)
  }

  setMarker("firstName", first ?? displayName)
  setMarker("lastName", last)
  setMarker("recipientName", displayName)
  setMarker("jobTitle", jobTitle ?? vacancyName ?? null)
  setMarker("company", companyName ?? null)
  setMarker("companyLocation", companyLocation ?? null)
  setMarker("vacancyName", vacancyName ?? jobTitle ?? null)
  setMarker("senderEmail", normalizedSender)
  setMarker("Locatie", location ?? null)
  setMarker("phoneNumber", phoneNumber ?? null)
  setMarker("tel", tel ?? null)
  setMarker("reception", reception ?? null)
  setMarker("officeFooter", officeFooter ?? null)
  // Advisor marker (HubSpot 'selectieadviseur')
  setMarker("Advisor", advisor ?? null)
  // AdvisorOrOffice: prefer advisor, otherwise when user team === 'office' use 'team office'
  if (advisor && advisor.trim().length > 0) {
    setMarker("AdvisorOrOffice", advisor)
  } else if (team === "office") {
    setMarker("AdvisorOrOffice", `${team} office`)
  }

  return Object.fromEntries(markers.entries())
}

const resolvePresetSchedule = (option: SchedulingOption) => {
  const now = new Date()

  if (option === "one-hour") {
    const inOneHour = new Date(now.getTime() + 60 * 60 * 1000)
    return inOneHour.toISOString()
  }

  if (option === "tomorrow-morning") {
    const tomorrow = new Date(now)
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(9, 0, 0, 0)
    return tomorrow.toISOString()
  }

  return now.toISOString()
}

export default function Vacature() {
  const { slug } = useParams<{ slug: string }>()
  const vacancyId = slug ?? ""
  const normalizedVacancyId = useMemo(
    () => normalizeHubspotId(vacancyId),
    [vacancyId]
  )
  const routeReady = Boolean(slug)

  const { data, loading, error } = useVacancyApplications(vacancyId)

  const vacancyTitle = useMemo(
    () => data?.vacancy?.title ?? data?.vacancy?.vacatureNaam ?? null,
    [data?.vacancy?.title, data?.vacancy?.vacatureNaam]
  )
  const companyName = useMemo(() => data?.company?.name ?? null, [data?.company?.name])
  const companyLocation = useMemo(() => data?.company?.location ?? null, [data?.company?.location])
  const hideCompanyInEmailDialog = shouldHideCompanyInEmail(data?.vacancy?.pipelineStage ?? null)
  const emailCompanyName = hideCompanyInEmailDialog ? null : companyName
  const emailCompanyLocation = hideCompanyInEmailDialog ? null : companyLocation

  const isLoading = !routeReady || loading

  const stageAssociations: StageAssociationLookup = useMemo(
    () => buildStageAssociationLookup(data?.associationLabels ?? []),
    [data?.associationLabels]
  )

  const statusHistoryContext = useMemo(
    () => ({
      vacancyName: vacancyTitle ?? null,
      vacancyTitle: vacancyTitle ?? null,
      vacancyLocation: data?.vacancy?.locatie ?? null,
      vacancyDiscipline: data?.vacancy?.vakgebied ?? null,
      companyName: companyName ?? null,
    }),
    [vacancyTitle, data?.vacancy?.locatie, data?.vacancy?.vakgebied, companyName]
  )

  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const searchParamsString = searchParams.toString()
  const currentViewParam = searchParams.get("view")
  const { syncStatusBatch } = useCandidateStatusUpdater()
  const [features, setFeatures] = useState<KanbanFeature[]>([])
  const initialViewMode = currentViewParam === "table" ? "table" : "kanban"
  const [viewMode, setViewMode] = useState<"kanban" | "table">(initialViewMode)
  const [isBelowTableBreakpoint, setIsBelowTableBreakpoint] = useState<boolean>(() =>
    getIsBelowTableViewBreakpoint()
  )
  const [searchTerm, setSearchTerm] = useState("")
  const [filters, setFilters] = useState<CandidateFilters>(createDefaultCandidateFilters())
  type PendingOverride = {
    column: CandidateStatusId
    statusLabel: string | null
    associationTypeId: string | null
    expiresAt: number
    pendingCount: number
  }
  const pendingOverridesRef = useRef<Record<string, PendingOverride>>({})
  const { user } = useAuth0()
  const getAccessToken = useAuth0AccessToken()
  const { templates, loading: templatesLoading } = useEmailTemplates()
  const hasTemplates = templates.length > 0
  const actorName = user?.nickname ?? user?.name ?? null
  const actorEmail = user?.email ?? null
  const actorId = user?.sub ?? null
  const team = user?.["https://ats.siebertwassink.nl/team"] as string | undefined
  const isOfficeTeam = team === "office"
  const officeClaim = user?.["https://ats.siebertwassink.nl/office"]
  const userOfficeLocation: OfficeLocation = isOfficeLocation(officeClaim)
    ? officeClaim
    : DEFAULT_OFFICE_LOCATION
  const OFFICE_FROM_EMAIL = import.meta.env.VITE_OFFICE_FROM_EMAIL ?? "office@siebertwassink.nl"
  const [bulkEmailDialogOpen, setBulkEmailDialogOpen] = useState(false)
  const [bulkRecipients, setBulkRecipients] = useState<EmailRecipient[]>([])
  const [bulkSelectedTemplate, setBulkSelectedTemplate] = useState<Template>("Custom")
  const [bulkMessageBody, setBulkMessageBody] = useState("")
  const [bulkSubjectLine, setBulkSubjectLine] = useState("")
  const [bulkFromEmail, setBulkFromEmail] = useState<string>(() =>
    isOfficeTeam ? OFFICE_FROM_EMAIL : actorEmail ?? OFFICE_FROM_EMAIL
  )
  const [bulkLocation, setBulkLocation] = useState<OfficeLocation>(userOfficeLocation)
  const [bulkIsSending, setBulkIsSending] = useState(false)
  const [bulkSendError, setBulkSendError] = useState<string | null>(null)

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }
    const scrollToBottom = () => {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: "auto",
      })
    }
    const frame = window.requestAnimationFrame(scrollToBottom)
    return () => window.cancelAnimationFrame(frame)
  }, [])

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    const handleResize = () => {
      setIsBelowTableBreakpoint(getIsBelowTableViewBreakpoint())
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  useEffect(() => {
    if (!hasTemplates) {
      return
    }
    setBulkSelectedTemplate((current) => {
      if (templates.some((template) => template.template === current)) {
        return current
      }
      return templates[0]?.template ?? current
    })
    if (isOfficeTeam) {
      setBulkFromEmail(OFFICE_FROM_EMAIL)
    } else {
      setBulkFromEmail((current) => current || actorEmail || OFFICE_FROM_EMAIL)
    }
  }, [actorEmail, hasTemplates, templates, isOfficeTeam, OFFICE_FROM_EMAIL])

  useEffect(() => {
    if (!hasTemplates) {
      return
    }
    const activeTemplate = templates.find(
      (template) => template.template === bulkSelectedTemplate
    )
    if (!activeTemplate) {
      return
    }
    setBulkMessageBody((current) => {
      if (bulkSelectedTemplate === "Custom") {
        return current && current.trim().length > 0 ? current : activeTemplate.body
      }
      return activeTemplate.body
    })
    setBulkSubjectLine((current) => {
      if (bulkSelectedTemplate === "Custom") {
        return current && current.trim().length > 0
          ? current
          : activeTemplate.subject ?? ""
      }
      return activeTemplate.subject ?? ""
    })
  }, [bulkSelectedTemplate, hasTemplates, templates])

  useEffect(() => {
    if (!bulkEmailDialogOpen) {
      setBulkSendError(null)
    }
  }, [bulkEmailDialogOpen])

  const applyOverrides = useCallback(
    (input: KanbanFeature[]): KanbanFeature[] => {
      const overrides = pendingOverridesRef.current
      const overrideKeys = Object.keys(overrides)
      if (!overrideKeys.length) {
        const hasPending = input.some((feature) => feature.isStatusOverridePending === true)
        if (!hasPending) {
          return input
        }
        return input.map((feature) =>
          feature.isStatusOverridePending === true
            ? { ...feature, isStatusOverridePending: false }
            : feature
        )
      }

      const now = Date.now()
      const nextOverrides: Record<string, PendingOverride> = { ...overrides }
      let changed = false

      const merged = input.map((feature) => {
        const override = overrides[feature.id]
        if (!override) {
          if (feature.isStatusOverridePending === true) {
            changed = true
            return { ...feature, isStatusOverridePending: false }
          }
          return feature
        }

        const associationTypeMatch =
          override.associationTypeId == null ||
          override.associationTypeId === (feature.associationTypeId ?? null)
        const labelMatch =
          override.statusLabel == null || override.statusLabel === feature.statusLabel
        const columnMatch = feature.column === override.column
        const matchesServer = associationTypeMatch && labelMatch && columnMatch
        const isExpired = override.expiresAt <= now
        const isPending = override.pendingCount > 0

        if (isExpired || (!isPending && matchesServer)) {
          delete nextOverrides[feature.id]
          if (feature.isStatusOverridePending === true || isExpired) {
            changed = true
            return { ...feature, isStatusOverridePending: false }
          }
          changed = true
          return feature
        }

        changed = true
        nextOverrides[feature.id] = override
        return {
          ...feature,
          column: override.column,
          statusLabel: override.statusLabel ?? feature.statusLabel,
          associationTypeId: override.associationTypeId ?? feature.associationTypeId ?? null,
          isStatusOverridePending: isPending,
        }
      })

      // keep overrides for features not in current list (e.g. filtered out)
      overrideKeys.forEach((key) => {
        if (!(key in nextOverrides) && overrides[key] && overrides[key].expiresAt > now) {
          nextOverrides[key] = overrides[key]
        }
      })

      pendingOverridesRef.current = nextOverrides
      return changed ? merged : input
    },
    []
  )

  const refreshOverrides = useCallback(() => {
    setFeatures((previous) => {
      const merged = applyOverrides(previous)
      return merged === previous ? previous : merged
    })
  }, [applyOverrides])

  const registerPendingOverrides = useCallback(
    (entries: Array<{ featureId: string; column: CandidateStatusId; statusLabel: string | null; associationTypeId: string | null }>) => {
      if (!entries.length) {
        return
      }
      const now = Date.now()
      const overrides = { ...pendingOverridesRef.current }
      let changed = false

      entries.forEach(({ featureId, column, statusLabel, associationTypeId }) => {
        const previous = overrides[featureId]
        const pendingCount = (previous?.pendingCount ?? 0) + 1
        overrides[featureId] = {
          column,
          statusLabel,
          associationTypeId,
          expiresAt: now + 5000,
          pendingCount,
        }
        changed = true
      })

      if (changed) {
        pendingOverridesRef.current = overrides
        refreshOverrides()
      }
    },
    [refreshOverrides]
  )

  const settlePendingOverrides = useCallback(
    (ids: string[], success: boolean) => {
      if (!ids.length) {
        return
      }
      const overrides = { ...pendingOverridesRef.current }
      const now = Date.now()
      let changed = false

      ids.forEach((id) => {
        const entry = overrides[id]
        if (!entry) {
          return
        }
        if (!success) {
          delete overrides[id]
          changed = true
          return
        }
        const nextCount = Math.max(entry.pendingCount - 1, 0)
        if (nextCount === 0) {
          delete overrides[id]
          changed = true
          return
        }
        overrides[id] = {
          ...entry,
          pendingCount: nextCount,
          expiresAt: Math.max(entry.expiresAt, now + 1500),
        }
        changed = true
      })

      if (changed) {
        pendingOverridesRef.current = overrides
        refreshOverrides()
      }
    },
    [refreshOverrides]
  )
  const handleBulkStatusChange = useCallback(
    (ids: string[], statusId: CandidateStatusId) => {
      if (ids.length === 0) {
        return
      }

      const previousSnapshot = cloneFeatures(features)
      const idSet = new Set(ids)
      const updated = features.map((feature) => {
        if (!idSet.has(feature.id)) {
          return feature
        }
        const association = stageAssociations[statusId]
        const candidateLabel = association?.label ?? feature.statusLabel ?? null
        const associationTypeId = association?.typeId ?? feature.associationTypeId ?? null
        return {
          ...feature,
          column: statusId,
          statusLabel: candidateLabel,
          associationTypeId,
        }
      })

      setFeatures(updated)

      registerPendingOverrides(
        updated
          .filter((feature) => idSet.has(feature.id))
          .map((feature) => ({
            featureId: feature.id,
            column: feature.column,
            statusLabel: feature.statusLabel ?? null,
            associationTypeId: feature.associationTypeId ?? null,
          }))
      )

      const previousById = new Map(previousSnapshot.map((feature) => [feature.id, feature]))
      const pendingUpdates = Array.from(idSet).map((id) => {
        const feature = updated.find((item) => item.id === id)
        if (!feature) {
          return null
        }
        const previous = previousById.get(id)
        return {
          feature,
          nextStatus: statusId,
          previousFeature: previous,
          fallbackVacancyId: normalizedVacancyId,
          stageAssociations,
        }
      })
      const validUpdates = pendingUpdates.filter(
        (item): item is NonNullable<typeof item> => Boolean(item)
      )

      if (!validUpdates.length) {
        return
      }

      void (async () => {
        const results = await syncStatusBatch(validUpdates)
        const shouldRevert = results.some((result) => result.status !== "fulfilled")
        if (shouldRevert) {
          settlePendingOverrides(Array.from(idSet), false)
          console.warn("[vacature] reverting UI after bulk status sync issues", {
            results: results.map((result) => ({
              featureId: result.feature.id,
              status: result.status,
              reason:
                result.status === "fulfilled"
                  ? undefined
                  : result.reason instanceof Error
                    ? result.reason.message
                    : String(result.reason),
            })),
          })
          setFeatures(cloneFeatures(previousSnapshot))
          return
        }

        const fulfilled = results.filter(
          (
            result
          ): result is Extract<CandidateStatusUpdateOutcome, { status: "fulfilled" }> =>
            result.status === "fulfilled"
        )

        if (!fulfilled.length) {
          settlePendingOverrides(Array.from(idSet), true)
          return
        }

        const fulfilledMap = new Map(
          fulfilled.map((entry) => [entry.feature.id, entry] as const)
        )

        setFeatures((current) =>
          current.map((feature) => {
            const applied = fulfilledMap.get(feature.id)
            if (!applied) {
              return feature
            }
            const association = stageAssociations[feature.column]
            return {
              ...feature,
              statusLabel: applied.appliedLabel,
              associationTypeId:
                applied.associationTypeId ??
                association?.typeId ??
                feature.associationTypeId ??
                null,
            }
          })
        )
        settlePendingOverrides(Array.from(idSet), true)
      })()
    },
    [features, normalizedVacancyId, stageAssociations, syncStatusBatch]
  )

  const effectiveViewMode = isBelowTableBreakpoint ? "table" : viewMode

  const handleOpenCandidate = useCallback(
    (candidate: KanbanFeature) => {
      const targetPath =
        slug && slug.length > 0
          ? `/vacature/${vacancyId}/kandidaat/${candidate.id}`
          : `/kandidaat/${candidate.id}`

      navigate(targetPath, {
        state: {
          candidate,
          applicationId: candidate.applicationId ?? null,
          vacancyId: vacancyId ?? null,
          vacancyTitle,
          companyName,
          vacancySlug: slug ?? null,
          originView: effectiveViewMode,
          stageAssociations,
        },
      })
    },
    [
      navigate,
      slug,
      vacancyId,
      effectiveViewMode,
      stageAssociations,
      vacancyTitle,
      companyName,
    ]
  )

  useEffect(() => {
    setFeatures([]);
    pendingOverridesRef.current = {}
  }, [vacancyId]);

  useEffect(() => {
    if (!data?.applications) {
      if (!loading) {
        setFeatures([]);
      }
      return;
    }

    const mapped = mapApplicationsToKanbanFeatures(data.applications, data.company, {
      fallbackVacancyId: vacancyId,
      stageAssociations,
    })
    setFeatures(applyOverrides(mapped))
  }, [data?.applications, data?.company, loading, vacancyId, stageAssociations, applyOverrides]);


  const stats: KanbanStats = useMemo(() => calculateKanbanStats(features), [features])
  const normalizedSearch = searchTerm.trim().toLowerCase()
  const filteredFeatures = useMemo(() => {
    return features.filter((feature) => {
      if (!matchesCandidateFilters(feature, filters)) {
        return false
      }

      if (!normalizedSearch) {
        return true
      }

      const target = `${feature.name} ${feature.owner?.name ?? ""}`.toLowerCase()
      return target.includes(normalizedSearch)
    })
  }, [features, normalizedSearch, filters])

  const bulkMarkerValues = useMemo<MarkerMap>(() => {
    const primaryRecipient = bulkRecipients[0]
    if (primaryRecipient?.markers && Object.keys(primaryRecipient.markers).length > 0) {
      return primaryRecipient.markers
    }
    if (!primaryRecipient) {
      return {}
    }
    const officeDetails = OFFICE_LOCATIONS[bulkLocation]
    return buildMarkerMap({
      name: primaryRecipient.name,
      email: primaryRecipient.email,
      jobTitle: vacancyTitle,
      companyName: emailCompanyName,
      companyLocation: emailCompanyLocation ?? null,
      vacancyName: vacancyTitle,
      senderEmail: bulkFromEmail,
      location: officeDetails.address,
      phoneNumber: officeDetails.phone,
      tel: officeDetails.tel,
      reception: officeDetails.reception,
      officeFooter: buildOfficeFooter(bulkLocation),
      advisor: data?.vacancy?.advisor ?? null,
      team,
    })
  }, [
    bulkFromEmail,
    bulkLocation,
    bulkRecipients,
    emailCompanyName,
    emailCompanyLocation,
    vacancyTitle,
    data?.vacancy?.advisor,
    team,
  ])

  const bulkFromEmailOptions = useMemo(() => {
    if (isOfficeTeam) {
      return [OFFICE_FROM_EMAIL]
    }
    const options = new Set<string>()
    if (bulkFromEmail) {
      options.add(bulkFromEmail)
    }
    if (actorEmail) {
      options.add(actorEmail)
    }
    return Array.from(options)
  }, [OFFICE_FROM_EMAIL, actorEmail, bulkFromEmail, isOfficeTeam])

  const bulkFromEmailDisabled = true

  useEffect(() => {
    if (bulkRecipients.length === 0) {
      return
    }
    const officeDetails = OFFICE_LOCATIONS[bulkLocation]
    setBulkRecipients((current) =>
      current.map((recipient) => {
        const nextMarkers = {
          ...(recipient.markers ?? {}),
          Locatie: officeDetails.address,
          phoneNumber: officeDetails.phone,
          tel: officeDetails.tel,
          reception: officeDetails.reception,
          officeFooter: buildOfficeFooter(bulkLocation),
          senderEmail:
            bulkFromEmail ||
            recipient.markers?.senderEmail ||
            actorEmail ||
            DEFAULT_SENDER_EMAIL,
          ...(data?.vacancy?.advisor
            ? { Advisor: data.vacancy.advisor }
            : {}),
          ...(
            data?.vacancy?.advisor
              ? { AdvisorOrOffice: data.vacancy.advisor }
              : team === "office"
              ? { AdvisorOrOffice: `${team} office` }
              : {}
          ),
        }
        return {
          ...recipient,
          markers: nextMarkers,
        }
      })
    )
  }, [
    actorEmail,
    bulkFromEmail,
    bulkLocation,
    bulkRecipients.length,
    data?.vacancy?.advisor,
    team,
  ])

  const queueBulkEmail = useCallback(
    async (schedule?: SchedulePayload | null) => {
      if (bulkRecipients.length === 0) {
        return
      }

      setBulkIsSending(true)
      setBulkSendError(null)

      try {
        const templateMeta =
          templates.find((template) => template.template === bulkSelectedTemplate) ??
          templates[0] ??
          null
        const resolvedTemplateName = templateMeta?.template ?? bulkSelectedTemplate
        const resolvedSubject =
          bulkSubjectLine.trim().length > 0
            ? bulkSubjectLine.trim()
            : templateMeta?.subject ?? null

        const accessToken = await getAccessToken()
        await sendCandidateInvite({
          template: resolvedTemplateName,
          templateId: templateMeta?.id ?? null,
          body: bulkMessageBody,
          recipients: bulkRecipients,
          candidateName: bulkRecipients[0]?.name ?? null,
          jobTitle: vacancyTitle ?? null,
          company: emailCompanyName ?? null,
          applicationId: null,
          applicationKey: null,
          schedule,
          subject: resolvedSubject,
          actor: {
            name: actorName,
            email: actorEmail,
            userId: actorId,
          },
          fromEmail: bulkFromEmail,
          markers: {
            Locatie: OFFICE_LOCATIONS[bulkLocation].address,
            phoneNumber: OFFICE_LOCATIONS[bulkLocation].phone,
            tel: OFFICE_LOCATIONS[bulkLocation].tel,
            reception: OFFICE_LOCATIONS[bulkLocation].reception,
            officeFooter: buildOfficeFooter(bulkLocation),
          },
          accessToken,
          hideCompanyInEmail: hideCompanyInEmailDialog,
        })
        setBulkEmailDialogOpen(false)
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Onbekende fout bij het versturen van de e-mail"
        setBulkSendError(message)
        throw error
      } finally {
        setBulkIsSending(false)
      }
    },
    [
      actorEmail,
      actorId,
      actorName,
      bulkMessageBody,
      bulkRecipients,
      bulkSelectedTemplate,
      bulkSubjectLine,
      emailCompanyName,
      getAccessToken,
      hideCompanyInEmailDialog,
      templates,
      vacancyTitle,
      bulkLocation,
    ]
  )

  const handleBulkSendEmail = useCallback(async () => {
    try {
      await queueBulkEmail(null)
    } catch {
      // error already captured in state
    }
  }, [queueBulkEmail])

  const handleBulkScheduleOption = useCallback(
    async (option: SchedulingOption) => {
      const scheduledAt = resolvePresetSchedule(option)
      await queueBulkEmail({
        kind: "preset",
        option,
        scheduledAt,
      })
    },
    [queueBulkEmail]
  )

  const handleBulkScheduleCustom = useCallback(
    async (scheduledAt: Date) => {
      await queueBulkEmail({
        kind: "custom",
        scheduledAt: scheduledAt.toISOString(),
      })
    },
    [queueBulkEmail]
  )

  const handleBulkRemoveRecipient = useCallback((recipientId: string) => {
    setBulkRecipients((current) =>
      current.filter((recipient) => recipient.id !== recipientId)
    )
  }, [])

  const handleBulkEmailRequest = useCallback(
    (selectedCandidates: KanbanFeature[]) => {
      if (!hasTemplates) {
        return
      }

      const byId = new Map<string, EmailRecipient>()
      selectedCandidates.forEach((candidate) => {
        const email = candidate.email?.trim()
        if (!email) {
          return
        }
        if (byId.has(candidate.id)) {
          return
        }
        const name =
          candidate.name && candidate.name.trim().length > 0
            ? candidate.name.trim()
            : email
        const markers = buildMarkerMap({
          name,
          email,
          jobTitle: vacancyTitle,
          companyName: emailCompanyName,
          companyLocation: emailCompanyLocation ?? null,
          vacancyName: vacancyTitle,
          senderEmail: bulkFromEmail,
          location: OFFICE_LOCATIONS[bulkLocation].address,
          phoneNumber: OFFICE_LOCATIONS[bulkLocation].phone,
          tel: OFFICE_LOCATIONS[bulkLocation].tel,
          reception: OFFICE_LOCATIONS[bulkLocation].reception,
          officeFooter: buildOfficeFooter(bulkLocation),
          advisor: data?.vacancy?.advisor ?? null,
          team,
        })

        byId.set(candidate.id, {
          id: candidate.id,
          name,
          email,
          applicationId: candidate.applicationId ?? null,
          candidateId: candidate.contactId ?? null,
          vacancyId: candidate.vacancyId ?? vacancyId ?? null,
          markers,
          vacatureNaam: vacancyTitle ?? null,
        })
      })

      const recipients = Array.from(byId.values())
      if (!recipients.length) {
        return
      }

      setBulkRecipients(recipients)
      const fallbackTemplate = templates.some(
        (template) => template.template === bulkSelectedTemplate
      )
        ? bulkSelectedTemplate
        : templates[0]?.template ?? bulkSelectedTemplate

      setBulkSelectedTemplate(fallbackTemplate)
      const matchedTemplate = templates.find(
        (template) => template.template === fallbackTemplate
      )
      if (fallbackTemplate === "Custom") {
        setBulkMessageBody((current) =>
          current && current.trim().length > 0 ? current : matchedTemplate?.body ?? ""
        )
        setBulkSubjectLine((current) =>
          current && current.trim().length > 0 ? current : matchedTemplate?.subject ?? ""
        )
      } else {
        setBulkMessageBody(matchedTemplate?.body ?? "")
        setBulkSubjectLine(matchedTemplate?.subject ?? "")
      }
      setBulkFromEmail(isOfficeTeam ? OFFICE_FROM_EMAIL : actorEmail ?? OFFICE_FROM_EMAIL)
      setBulkLocation(userOfficeLocation)
      setBulkEmailDialogOpen(true)
    },
    [
      actorEmail,
      bulkSelectedTemplate,
      emailCompanyName,
      hasTemplates,
      templates,
      vacancyId,
      vacancyTitle,
      isOfficeTeam,
      OFFICE_FROM_EMAIL,
    ]
  )

  const bulkEmailActionDisabled = templatesLoading || !hasTemplates

  useEffect(() => {
    const expectedViewParam =
      isBelowTableBreakpoint || viewMode === "table" ? "table" : null

    if (expectedViewParam === "table" && currentViewParam !== "table") {
      const next = new URLSearchParams(searchParamsString)
      next.set("view", "table")
      setSearchParams(next, { replace: true })
      return
    }

    if (expectedViewParam === null && currentViewParam !== null) {
      const next = new URLSearchParams(searchParamsString)
      next.delete("view")
      setSearchParams(next, { replace: true })
    }
  }, [
    isBelowTableBreakpoint,
    viewMode,
    currentViewParam,
    searchParamsString,
    setSearchParams,
  ])

  const handleViewModeChange = useCallback(
    (mode: "kanban" | "table") => {
      if (isBelowTableBreakpoint) {
        return
      }
      if (mode === viewMode) {
        return
      }
      setViewMode(mode)
      const next = new URLSearchParams(searchParamsString)
      if (mode === "kanban") {
        next.delete("view")
      } else {
        next.set("view", mode)
      }
      setSearchParams(next, { replace: true })
    },
    [isBelowTableBreakpoint, searchParamsString, setSearchParams, viewMode]
  )

  return (
    <>
      <div className="flex h-screen flex-col">
        <div className="flex flex-col gap-6 p-6 lg:flex-row lg:items-start lg:justify-between">
          <header className="max-w-xl">
            {isLoading ? (
              <div className="mt-2" role="status" aria-live="polite">
                <span className="h-10 w-64 max-w-xl sm:w-96 skeleton-shimmer" aria-hidden="true" />
                <span className="sr-only">Vacaturetitel wordt geladen</span>
              </div>
            ) : error ? (
              <span className="text-red-500 ">Kon vacature niet laden</span>
            ) : (
              <h1 className="mt-2 text-3xl font-semibold">{data?.vacancy?.vacatureNaam} - {data?.company?.name} - {data?.vacancy?.locatie} </h1>
            )}
          </header>

          <div className="grid gap-4 stats-grid grid-cols-1 sm:grid-cols-2 ">
            <StatCard count={stats.kandidaten} text="Kandidaten" icon={UsersIcon} />
            <StatCard count={stats.inGesprek} text="In gesprek" icon={ActivityIcon} />
            <StatCard count={stats.aangenomen} text="Aangenomen" icon={CheckCircleIcon} />
            <StatCard count={stats.afgewezen} text="Afgewezen" icon={XCircleIcon} />
          </div>
        </div>
        <div className="flex flex-1 min-h-0 flex-col">
          <ViewControls
            className="px-6 pb-4"
            mode={effectiveViewMode}
            onModeChange={handleViewModeChange}
            search={searchTerm}
            onSearchChange={setSearchTerm}
            filters={filters}
            onFiltersChange={setFilters}
            onFiltersReset={() => setFilters(createDefaultCandidateFilters())}
            disableModeToggle={isBelowTableBreakpoint}
          />
          <div className="flex flex-1 min-h-0">
            {effectiveViewMode === "kanban" ? (
              <Kanban
                features={features}
                onFeaturesChange={setFeatures}
                filterQuery={searchTerm}
                filters={filters}
                onOpenCandidate={handleOpenCandidate}
                isLoading={isLoading}
                fallbackVacancyId={normalizedVacancyId}
                stageAssociations={stageAssociations}
                historyContext={statusHistoryContext}
                onStatusOverride={({ feature, nextStatus, statusLabel, associationTypeId }) =>
                  registerPendingOverrides([
                    {
                      featureId: feature.id,
                      column: nextStatus,
                      statusLabel,
                      associationTypeId,
                    },
                  ])
                }
                onStatusSettled={({ ids, success }) => settlePendingOverrides(ids, success)}
                onRequestEmail={handleBulkEmailRequest}
                bulkEmailDisabled={bulkEmailActionDisabled}
              />
            ) : (
              <CandidateTable
                features={filteredFeatures}
                onBulkStatusChange={handleBulkStatusChange}
                onOpenCandidate={handleOpenCandidate}
                onBulkEmail={handleBulkEmailRequest}
                bulkEmailDisabled={bulkEmailActionDisabled}
              />
            )}
          </div>
        </div>
      </div>
      <EmailSendDialog
        open={bulkEmailDialogOpen}
        onOpenChange={setBulkEmailDialogOpen}
        recipients={bulkRecipients}
        onRemoveRecipient={handleBulkRemoveRecipient}
        templates={templates}
        selectedTemplate={bulkSelectedTemplate}
        onTemplateChange={setBulkSelectedTemplate}
        messageBody={bulkMessageBody}
        onMessageBodyChange={setBulkMessageBody}
        subjectLine={bulkSubjectLine}
        onSubjectChange={setBulkSubjectLine}
        onSend={handleBulkSendEmail}
        onSchedule={handleBulkScheduleOption}
        onScheduleCustom={handleBulkScheduleCustom}
        isSending={bulkIsSending}
        errorMessage={bulkSendError}
        availableMarkers={bulkMarkerValues}
        hideCompanyFallback={hideCompanyInEmailDialog}
        location={bulkLocation}
        onLocationChange={setBulkLocation}
        fromEmail={bulkFromEmail}
        fromEmailOptions={bulkFromEmailOptions}
        onFromEmailChange={setBulkFromEmail}
        fromEmailDisabled={bulkFromEmailDisabled}
      />
    </>
  )
}
